import { useCallback, useEffect, useRef, useState } from 'react'
import { getDashboard } from '../core/api'
import { DEFAULT_REFRESH_MS, REFRESH_STORAGE_KEY } from '../core/consts'
import { DashboardSnapshot } from '../core/types'

export function readStoredInterval(): number {
  const raw = localStorage.getItem(REFRESH_STORAGE_KEY)
  const n = raw !== null ? parseInt(raw, 10) : NaN
  return Number.isFinite(n) && n >= 0 ? n : DEFAULT_REFRESH_MS
}

export interface DashboardPolling {
  data: DashboardSnapshot | null
  error: string | null
  loading: boolean // first load only (data still null)
  refreshing: boolean // any fetch in flight (incl. manual refresh)
  lastUpdated: number | null
  intervalMs: number
  setIntervalMs: (ms: number) => void
  refresh: () => void
}

/**
 * Polls the matching dashboard snapshot at a user-selected interval.
 * - interval persisted in localStorage; 0 = paused
 * - background tabs are skipped (visibilitychange), refetch on refocus
 * - timer + listener cleaned up on unmount
 *
 * The interval is a self-rescheduling timeout anchored on the END of the previous
 * fetch, not a setInterval anchored on mount (CPAYMENT-1689). With setInterval a
 * snapshot that took 9 s was followed by the next tick 11 s later — the page
 * looked like it fired requests in pairs, and a tick that arrived mid-flight was
 * silently dropped by the in-flight guard, so the real gap was unpredictable.
 */
export function useDashboardPolling(): DashboardPolling {
  const [intervalMs, setIntervalMsState] = useState<number>(readStoredInterval)
  const [data, setData] = useState<DashboardSnapshot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [refreshing, setRefreshing] = useState<boolean>(false)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const inFlight = useRef(false)

  const setIntervalMs = useCallback((ms: number) => {
    localStorage.setItem(REFRESH_STORAGE_KEY, String(ms))
    setIntervalMsState(ms)
  }, [])

  const fetchOnce = useCallback(async () => {
    if (document.hidden || inFlight.current) return
    inFlight.current = true
    setRefreshing(true)
    try {
      const resp = await getDashboard()
      if (resp.data.success) {
        setData(resp.data.data)
        setError(null)
      } else {
        setError('Не удалось получить данные дашборда')
      }
      setLastUpdated(Date.now())
    } catch {
      setError('Сервис мэтчинга недоступен')
    } finally {
      setLoading(false)
      setRefreshing(false)
      inFlight.current = false
    }
  }, [])

  // Single driver for the whole lifecycle: load once, then keep rescheduling from
  // the moment the previous load finished. On "Пауза" (0) only the initial load
  // runs and nothing is fetched again until the user hits manual refresh.
  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const schedule = () => {
      if (cancelled || intervalMs <= 0) return
      timer = setTimeout(run, intervalMs)
    }

    async function run() {
      await fetchOnce()
      schedule()
    }

    // Becoming visible refreshes immediately and restarts the cadence from there,
    // instead of leaving a stale timer that would fire right on top of this one.
    const onVisible = () => {
      if (document.hidden || cancelled) return
      if (timer !== undefined) clearTimeout(timer)
      void run()
    }
    document.addEventListener('visibilitychange', onVisible)

    void run()

    return () => {
      cancelled = true
      if (timer !== undefined) clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [intervalMs, fetchOnce])

  return { data, error, loading, refreshing, lastUpdated, intervalMs, setIntervalMs, refresh: fetchOnce }
}
